import { getQueryString, createAxiosInstance } from 'shared/api/apiUtils';
import { refreshRoutesAndConnections } from 'shared/api/cacheRefresh';
import {
  SourceFilter,
  SourceSummary,
} from 'routes/sourceFilters/sourceFilterTypes';

// Source filters (GUNDI-5178) — the whitelist/blacklist rules on a route's arrows.
//
// Nested under the route because a rule belongs to one route→destination pair. The
// devices are NOT inline: a rule may hold up to the backend's cap, so they are paged
// from the sources sub-resource below.

// Rethrows like create/update: a swallowed failure here renders as "no rules", and
// the user's next move is to recreate one and hit the duplicate 409. Pages through
// the whole set — one filter per destination, and routes can carry more destinations
// than a server page; a rule beyond page one would otherwise be invisible AND
// un-createable (its create 409s while nothing shows).
const LIST_PAGE_SIZE = 50;

export async function listRouteFilters(
  token: string,
  route_id: string,
): Promise<SourceFilter[]> {
  const axiosInstance = await createAxiosInstance(token);
  const all: SourceFilter[] = [];
  let offset = 0;
  for (;;) {
    const resp = await axiosInstance.get(
      `/v2/routes/${route_id}/filters/?pager=limit&limit=${LIST_PAGE_SIZE}&offset=${offset}`,
    );
    const results: SourceFilter[] = resp.data?.results ?? [];
    all.push(...results);
    offset += results.length;
    const count: number = resp.data?.count ?? all.length;
    if (all.length >= count || results.length === 0) return all;
  }
}

// Create and update both rethrow so the caller can read the response body: the backend
// answers 400 with a per-field message and 409 when the destination already carries a
// rule of this type, and both belong on the form rather than in a generic toast.
export async function createRouteFilter(
  token: string,
  route_id: string,
  payload: { destination: string; mode: string; source_ids: string[] },
): Promise<SourceFilter> {
  const axiosInstance = await createAxiosInstance(token);
  const resp = await axiosInstance.post(
    `/v2/routes/${route_id}/filters/`,
    payload,
  );
  refreshRoutesAndConnections();
  return resp.data;
}

// The device list is replaced wholesale — there is no per-device add or remove — so
// `source_ids` must carry every device the rule should end up with.
export async function updateRouteFilter(
  token: string,
  route_id: string,
  filter_id: string,
  payload: { mode?: string; source_ids?: string[]; enabled?: boolean },
): Promise<SourceFilter> {
  const axiosInstance = await createAxiosInstance(token);
  const resp = await axiosInstance.patch(
    `/v2/routes/${route_id}/filters/${filter_id}/`,
    payload,
  );
  refreshRoutesAndConnections();
  return resp.data;
}

// Rethrows: swallowing left the caller confirming a delete that never happened.
export async function deleteRouteFilter(
  token: string,
  route_id: string,
  filter_id: string,
): Promise<void> {
  const axiosInstance = await createAxiosInstance(token);
  await axiosInstance.delete(`/v2/routes/${route_id}/filters/${filter_id}/`);
  refreshRoutesAndConnections();
}

// The rule's own devices. Limit/offset rather than the project default page size: the
// backend serves 50 here, and a chip list wants a bigger page than a table does.
// Rethrows: a swallowed page here silently TRUNCATES the editor's draft, and the
// wholesale save would then wipe the missing devices from the rule.
export async function getRouteFilterSources(
  token: string,
  route_id: string,
  filter_id: string,
  query: object = {},
): Promise<{ results: SourceSummary[]; count: number }> {
  const axiosInstance = await createAxiosInstance(token);
  const query_string = getQueryString(query);
  const resp = await axiosInstance.get(
    `/v2/routes/${route_id}/filters/${filter_id}/sources/?pager=limit` +
      (query_string ? `&${query_string}` : ''),
  );
  return { results: resp.data?.results ?? [], count: resp.data?.count ?? 0 };
}
